/* Native dialogs provide focus trapping and Escape handling on desktop and mobile. */
(() => {
  if (window.sekageNavigationReady) return;
  window.sekageNavigationReady = true;
  const openers = new WeakMap();
  const lock = () => document.documentElement.classList.toggle('sk-dialog-open', !!document.querySelector('.sk-dialog[open], .sk-header__account[data-sk-account-open]'));

  const header = document.querySelector('[data-sk-header]');
  const headerSection = header?.closest('.shopify-section--header');
  const announcement = document.querySelector('[data-sk-announcement]');
  const account = header?.querySelector('.sk-header__account');
  // The transparent bar always uses white controls. Pointer or keyboard interaction
  // gives the entire bar its solid surface without inspecting the photograph.
  let headerHovered = false;
  let headerFocused = false;
  const syncHeaderSurface = () => header?.toggleAttribute('data-sk-header-active', headerHovered || headerFocused);
  header?.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { headerHovered = true; syncHeaderSurface(); } });
  header?.addEventListener('pointerleave', event => { if (event.pointerType === 'mouse') { headerHovered = false; syncHeaderSurface(); } });
  header?.addEventListener('focusin', () => { headerFocused = true; syncHeaderSurface(); });
  header?.addEventListener('focusout', event => {
    if (header.contains(event.relatedTarget)) return;
    headerFocused = false;
    syncHeaderSurface();
  });
  let accountDialogObserver;
  account?.addEventListener('open', () => {
    // Mirror the native dialog's closing state for the outward slide; Shopify
    // continues to own the dialog, focus management and sign-in controls.
    if (!accountDialogObserver) {
      const nativeDialog = account.shadowRoot?.querySelector('dialog[part~="dialog"]');
      if (nativeDialog) {
        accountDialogObserver = new MutationObserver(() => {
          account.toggleAttribute('data-sk-account-closing', nativeDialog.classList.contains('closing'));
        });
        accountDialogObserver.observe(nativeDialog, { attributes: true, attributeFilter: ['class'] });
      }
    }
    account.removeAttribute('data-sk-account-closing');
    account.setAttribute('data-sk-account-open', '');
    lock();
  });
  account?.addEventListener('close', () => {
    account.removeAttribute('data-sk-account-open');
    lock();
  });
  // The header group (announcement + main bar) is pinned as one unit (theme.liquid, .sk-chrome).
  // Docked, the announcement scrolls away with the page while the main bar stays at the top. Over a
  // dark hero that opens the page, the main bar is transparent until half the hero has passed.
  // On phones the whole group slides away past that point while scrolling down, comes back solid on
  // the first scroll up, and turns transparent again back in the top half of the hero.
  const chrome = document.querySelector('[data-sk-chrome]');
  const announcementSection = announcement?.closest('.shopify-section');
  if (chrome && header && headerSection) {
    const root = document.documentElement;
    // Phones in portrait, and phones in landscape (short, touch-driven screens) where pinned bars would
    // take a quarter of the view, both get the hide-on-scroll behaviour.
    const phone = matchMedia('(max-width: 767px), (max-height: 500px) and (pointer: coarse)');
    const chromeReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let hero = null;
    let annH = 0;
    let barH = 0;
    let threshold = 0;
    let lastY = Math.max(0, scrollY);
    let mode = 'docked';
    let queued = false;
    let glideTimer;
    const measure = () => {
      hero = document.querySelector('[data-sk-header-overlay]');
      const heroImage = hero?.querySelector('img');
      if (heroImage && !heroImage.complete) heroImage.addEventListener('load', () => remeasure(), { once: true });
      annH = announcementSection ? announcementSection.getBoundingClientRect().height : 0;
      barH = headerSection.getBoundingClientRect().height;
      root.style.setProperty('--sk-ann-h', `${annH}px`);
      root.style.setProperty('--sk-bar-h', `${barH}px`);
      // Half the hero; without one, just past the bars themselves.
      threshold = hero ? hero.getBoundingClientRect().top + scrollY + hero.offsetHeight / 2 : annH + barH * 2;
    };
    const setMode = next => {
      if (next === mode) return;
      mode = next;
      // A change of mode glides; the docked position tracks the scroll exactly, so it never does.
      if (chromeReducedMotion.matches) return;
      chrome.classList.add('sk-chrome--gliding');
      clearTimeout(glideTimer);
      glideTimer = setTimeout(() => chrome.classList.remove('sk-chrome--gliding'), 340);
    };
    const apply = y => {
      const shift = mode === 'hidden' ? -(annH + barH + 12) : mode === 'revealed' ? 0 : -Math.min(y, annH);
      chrome.style.transform = shift ? `translate3d(0, ${shift}px, 0)` : '';
      // Clear over the top half of a hero is the stylesheet's default, so the bar never flashes black on load.
      const clear = !!hero && y < threshold;
      root.classList.toggle('sk-chrome-solid', !clear);
      chrome.toggleAttribute('data-sk-chrome-hidden', mode === 'hidden');
      chrome.toggleAttribute('data-sk-chrome-floating', mode !== 'hidden' && y > annH);
    };
    const place = () => {
      queued = false;
      const y = Math.max(0, scrollY);
      const delta = y - lastY;
      lastY = y;
      if (!phone.matches || y <= annH) setMode('docked');
      else if (delta > 4 && y > threshold) setMode('hidden');
      else if (delta < -4) setMode('revealed');
      apply(y);
    };
    const schedule = () => { if (!queued) { queued = true; requestAnimationFrame(place); } };
    const remeasure = () => { measure(); apply(Math.max(0, scrollY)); };
    // Tabbing into a hidden bar brings it back.
    chrome.addEventListener('focusin', () => { if (mode === 'hidden') { setMode('revealed'); apply(Math.max(0, scrollY)); } });
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', remeasure, { passive: true });
    addEventListener('load', remeasure);
    phone.addEventListener('change', remeasure);
    if ('ResizeObserver' in window) {
      const sizes = new ResizeObserver(remeasure);
      sizes.observe(chrome);
      const firstHero = document.querySelector('[data-sk-header-overlay]');
      if (firstHero) sizes.observe(firstHero);
    }
    document.addEventListener('shopify:section:load', remeasure);
    measure();
    place();
  }

  const dialogReducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const slidingDialogs = '.sk-drawer, .sk-search, .sk-cart-drawer, .sk-features-drawer';
  const setExpanded = (dialog, expanded) => document.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach(control => control.setAttribute('aria-expanded', String(expanded)));

  // Sliding panels run on springs (sekage-motion.js). They open and close from wherever they are on
  // screen, follow a finger that drags them toward their edge, and can be caught on the way out and
  // pulled back. Without that script, or with reduced motion, the CSS keyframes below take over.
  const motion = window.SekageMotion;
  const narrow = matchMedia('(max-width: 767px)');
  const panels = new WeakMap();
  const physical = dialog => !!motion && !dialogReducedMotion.matches && dialog.matches(slidingDialogs);
  const panel = dialog => {
    if (!panels.has(dialog)) panels.set(dialog, { x: 0, anim: null, closing: false });
    return panels.get(dialog);
  };
  // The edge a panel leaves by, the same one it came in from: left for the menu (and search on phones,
  // where its button sits left of the logo), right for search and the cart elsewhere.
  const exitSide = dialog => dialog.matches('.sk-drawer:not(.sk-account-panel)') || (dialog.matches('.sk-search') && narrow.matches) ? -1 : 1;
  function setPanel(dialog, x) {
    panel(dialog).x = x;
    dialog.style.transform = x ? `translate3d(${x}px, 0, 0)` : '';
    // The scrim dims in step with how much of the panel is showing.
    const shown = 1 - Math.min(1, Math.abs(x) / (dialog.offsetWidth || 1));
    dialog.style.setProperty('--sk-scrim', shown.toFixed(3));
  }
  function glide(dialog, to, { velocity, damping = 1, response = 0.36, done } = {}) {
    const state = panel(dialog);
    // Interrupting a glide keeps its speed, so a redirected panel never stalls before turning.
    const speed = velocity ?? (state.anim?.running ? state.anim.velocity : 0);
    state.anim?.stop();
    state.anim = motion.spring({
      from: state.x, to, velocity: speed, damping, response,
      onUpdate: x => setPanel(dialog, x),
      onComplete: () => { state.anim = null; done?.(); },
    });
  }
  // Close buttons, the backdrop and Escape let a panel leave the way it came in. Links and section
  // unloads close at once, since the page is about to change anyway.
  function close(dialog, animate = false) {
    if (!dialog?.open) return;
    if (!animate || dialogReducedMotion.matches || !dialog.matches(slidingDialogs)) {
      dialog.close();
      return;
    }
    if (physical(dialog) && dialog.hasAttribute('data-sk-physics')) {
      const state = panel(dialog);
      if (state.closing) return;
      state.closing = true;
      glide(dialog, exitSide(dialog) * dialog.offsetWidth, { response: 0.3, done: () => dialog.close() });
      return;
    }
    if (dialog.hasAttribute('data-sk-closing')) return;
    dialog.setAttribute('data-sk-closing', '');
    let backstop;
    const finish = event => {
      // The backdrop and children fire their own animationend; wait for the panel's own exit.
      if (event && (event.target !== dialog || event.pseudoElement || !/^sk-(drawer|search)-out/.test(event.animationName))) return;
      clearTimeout(backstop);
      dialog.removeEventListener('animationend', finish);
      dialog.close();
    };
    dialog.addEventListener('animationend', finish);
    backstop = setTimeout(finish, 400); // if animationend never arrives, still close
  }
  // Drag to dismiss (touch and pen). A pull toward the panel's own edge follows the finger 1:1 from where
  // it was grabbed; a pull the other way meets growing resistance. On release the throw is projected
  // forward the way scrolling decelerates: if it would carry the panel more than halfway out, it leaves
  // at the finger's speed, otherwise it springs back. A vertical move is a scroll and is left alone.
  function draggable(dialog) {
    const track = motion.tracker();
    let drag = null;
    let swallowUntil = 0;
    // A drag that ends over a link or the backdrop must not also count as a click on it.
    dialog.addEventListener('click', event => {
      if (performance.now() > swallowUntil) return;
      swallowUntil = 0;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    dialog.addEventListener('pointerdown', event => {
      if (!physical(dialog) || event.pointerType === 'mouse' || !event.isPrimary) return;
      // Account hub product rails scroll sideways themselves; a swipe there is not a drawer drag.
      if (event.target.closest('input, textarea, select, .sk-account__rail')) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right) return;
      const state = panel(dialog);
      // A panel in motion stops under the finger the moment it is touched.
      const caught = !!state.anim?.running;
      if (caught) { state.anim.stop(); state.anim = null; state.closing = false; }
      drag = { id: event.pointerId, x0: event.clientX, y0: event.clientY, from: state.x, active: false, caught };
      track.reset();
      track.add(event.clientX, event.clientY);
    });
    dialog.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      track.add(event.clientX, event.clientY);
      if (!drag.active) {
        const dx = event.clientX - drag.x0;
        const dy = event.clientY - drag.y0;
        if (Math.hypot(dx, dy) < 10) return;
        if (Math.abs(dy) > Math.abs(dx)) { if (!drag.caught) drag = null; return; }
        drag.active = true;
        drag.x0 = event.clientX; // track from here, so the panel doesn't jump the 10px it took to decide
        drag.from = panel(dialog).x;
        try { dialog.setPointerCapture(event.pointerId); } catch {}
        dialog.setAttribute('data-sk-dragging', '');
      }
      const side = exitSide(dialog);
      const width = dialog.offsetWidth;
      let x = drag.from + event.clientX - drag.x0;
      x = x * side < 0 ? -side * motion.rubberband(Math.abs(x), width) : side * Math.min(Math.abs(x), width);
      setPanel(dialog, x);
    });
    const release = event => {
      if (!drag || event.pointerId !== drag.id) return;
      track.add(event.clientX, event.clientY); // a finger that paused before lifting reads as still
      const { active, caught } = drag;
      drag = null;
      dialog.removeAttribute('data-sk-dragging');
      if (!active && !caught) return;
      // Neither a drag nor a touch that caught a moving panel is a tap on whatever lies under the finger.
      swallowUntil = performance.now() + 400;
      const side = exitSide(dialog);
      const width = dialog.offsetWidth;
      const state = panel(dialog);
      const velocity = active ? track.velocity().x : 0;
      const resting = state.x + motion.project(velocity);
      if (resting * side > width / 2) {
        state.closing = true;
        glide(dialog, side * width, { velocity, response: 0.3, done: () => dialog.close() });
      } else {
        // Thrown back open, it may overshoot a touch; eased back slowly, it settles without one.
        glide(dialog, 0, { velocity, damping: Math.abs(velocity) > 300 ? 0.8 : 1, response: 0.32 });
      }
    };
    dialog.addEventListener('pointerup', release);
    dialog.addEventListener('pointercancel', release);
  }
  function prepare(dialog) {
    if (dialog.dataset.skReady) return;
    dialog.dataset.skReady = 'true';
    if (motion && dialog.matches(slidingDialogs)) draggable(dialog);
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const targets = Array.from(dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'))
        .filter(element => !element.disabled && !element.closest('[inert]') && element.tabIndex >= 0 && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
      if (!targets.length) return;
      const first = targets[0];
      const last = targets[targets.length - 1];
      if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (dialog.matches('.sk-drawer') || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close(dialog, true);
    });
    dialog.addEventListener('close', () => {
      dialog.removeAttribute('data-sk-closing');
      const state = panels.get(dialog);
      if (state) { state.anim?.stop(); state.anim = null; state.closing = false; state.x = 0; }
      dialog.removeAttribute('data-sk-physics');
      dialog.removeAttribute('data-sk-dragging');
      dialog.style.removeProperty('transform');
      dialog.style.removeProperty('--sk-scrim');
      setExpanded(dialog, false);
      lock();
      const opener = openers.get(dialog);
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    // Escape plays the same exit. Browsers may refuse to let a page cancel a repeated Escape;
    // then the dialog simply closes at once, which is fine.
    dialog.addEventListener('cancel', event => {
      if (!event.cancelable || dialogReducedMotion.matches || !dialog.matches(slidingDialogs)) return;
      event.preventDefault();
      close(dialog, true);
    });
  }
  // The opener gets focus back on close: the header control that was clicked, or the Add to Cart
  // button that opened the cart drawer.
  function open(dialog, opener) {
    if (!dialog?.showModal) return;
    if (dialog.open) {
      // Asked to open while it is still leaving: it turns around from where it is.
      const state = panels.get(dialog);
      if (state?.closing) { state.closing = false; glide(dialog, 0); }
      return;
    }
    prepare(dialog);
    openers.set(dialog, opener);
    setExpanded(dialog, true);
    if (physical(dialog)) {
      dialog.setAttribute('data-sk-physics', '');
      dialog.showModal();
      setPanel(dialog, exitSide(dialog) * dialog.offsetWidth);
      glide(dialog, 0, { response: 0.4 });
    } else {
      dialog.showModal();
    }
    lock();
    dialog.querySelector('[data-sk-search-input]')?.focus();
  }
  // A product feature chip opens the features drawer at its own feature: that row starts at the top of the list.
  // The jump is instant (the panel is still arriving), so it needs no reduced-motion variant.
  function revealFeature(dialog, feature) {
    if (!feature || !dialog.matches('.sk-features-drawer')) return;
    const list = dialog.querySelector('.sk-features-drawer__list');
    const item = list?.querySelector(`li.sk-pdp-feature[data-sk-feature="${CSS.escape(feature)}"]`);
    // Measured on screen, so the row (icon tile included) lands just below the drawer head, clear of it.
    if (item) list.scrollTop += item.getBoundingClientRect().top - list.getBoundingClientRect().top - parseFloat(getComputedStyle(list).paddingTop || 0);
  }
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-sk-open]');
    if (trigger) {
      const dialog = document.getElementById(trigger.dataset.skOpen);
      if (!dialog?.showModal) return;
      event.preventDefault();
      open(dialog, trigger);
      revealFeature(dialog, trigger.dataset.skFeature);
      return;
    }
    const dismiss = event.target.closest('[data-sk-close]');
    if (dismiss) close(dismiss.closest('dialog'), true);
    const link = event.target.closest('.sk-dialog a[href]');
    if (link) close(link.closest('dialog'));
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.sk-dialog[open]').forEach(dialog => close(dialog));
    lock();
  });

  // Wallet categories form one adjacent drawer with the category that opens them.
  // Hover is a desktop shortcut; the disclosure also works by click, keyboard and touch.
  const initWalletCategories = () => {
    document.querySelectorAll('[data-sk-wallet-category]').forEach(category => {
      if (category.dataset.skWalletReady) return;
      category.dataset.skWalletReady = 'true';
      const toggle = category.querySelector('[data-sk-wallet-toggle]');
      const flyout = category.querySelector('[data-sk-wallet-flyout]');
      const drawer = category.closest('dialog');
      if (!toggle || !flyout || !drawer) return;
      const desktop = matchMedia('(min-width: 900px)');
      const hover = matchMedia('(min-width: 900px) and (hover: hover) and (pointer: fine)');
      const insideWallets = element => element instanceof Node && (category.contains(element) || flyout.contains(element));
      const setOpen = expanded => {
        toggle.setAttribute('aria-expanded', String(expanded));
        flyout.setAttribute('aria-hidden', String(!expanded));
        flyout.inert = !expanded;
        category.toggleAttribute('data-sk-wallet-open', expanded);
        drawer.toggleAttribute('data-sk-wallet-open', expanded);
      };
      // Keep the desktop pane outside the main scrolling body, so that neither its
      // overflow nor its scroll-edge mask can clip the adjacent pane. On phones the
      // same links return beneath their disclosure and scroll with the main menu.
      const placeFlyout = () => {
        setOpen(false);
        (desktop.matches ? drawer : category).append(flyout);
      };
      placeFlyout();
      toggle.addEventListener('click', event => {
        // A mouse enters before it clicks; keep the pane opened by that hover.
        // Keyboard and touch retain the disclosure's toggle behavior.
        const mouseOpened = hover.matches && event.detail > 0 && event.pointerType !== 'touch';
        setOpen(mouseOpened || toggle.getAttribute('aria-expanded') !== 'true');
      });
      const enter = event => { if (hover.matches && event.pointerType === 'mouse') setOpen(true); };
      const leave = event => {
        if (!hover.matches || event.pointerType !== 'mouse') return;
        if (insideWallets(event.relatedTarget)) return;
        if (flyout.contains(document.activeElement)) toggle.focus({ preventScroll:true });
        setOpen(false);
      };
      const focusOut = event => { if (!insideWallets(event.relatedTarget)) setOpen(false); };
      const keydown = event => {
        if (event.key === 'ArrowRight' && event.target === toggle) {
          event.preventDefault();
          setOpen(true);
          flyout.querySelector('a')?.focus();
        } else if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          toggle.focus();
        }
      };
      [category, flyout].forEach(surface => {
        surface.addEventListener('pointerenter', enter);
        surface.addEventListener('pointerleave', leave);
        surface.addEventListener('focusout', focusOut);
        surface.addEventListener('keydown', keydown);
      });
      flyout.querySelector('[data-sk-wallet-close]')?.addEventListener('click', () => { setOpen(false); toggle.focus(); });
      drawer.addEventListener('cancel', event => {
        if (toggle.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        setOpen(false);
        toggle.focus();
      });
      drawer.addEventListener('close', () => setOpen(false));
      desktop.addEventListener('change', placeFlyout);
      hover.addEventListener('change', () => setOpen(false));
    });
  };
  initWalletCategories();
  document.addEventListener('shopify:section:load', initWalletCategories);

  // Cart drawer. Add to Cart posts in the background, then the drawer opens with the updated cart;
  // quantity and remove controls inside it update in place. Every response carries a fresh render of
  // sections/sekage-cart-drawer.liquid (Section Rendering API), so items and totals always come from
  // Shopify. If the add request can't reach the store at all, the form falls back to its normal post.
  const cartDrawer = document.querySelector('[data-sk-cart-drawer]');
  const cartSection = 'sekage-cart-drawer';
  const shopRoot = window.Shopify?.routes?.root || '/';
  const cartRequest = (path, body) => fetch(shopRoot + path, {
    method: 'POST',
    headers: body instanceof FormData
      ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      : { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: body instanceof FormData ? body : JSON.stringify(body)
  }).then(async response => {
    const data = await response.json().catch(() => ({}));
    // A refusal from Shopify (sold out, not enough stock) carries a message for the shopper.
    if (!response.ok) throw Object.assign(new Error(data.description || data.message || ''), { status: response.status });
    return data;
  });
  function renderCart(sections) {
    const current = cartDrawer?.querySelector('[data-sk-cart-drawer-content]');
    const html = sections?.[cartSection];
    if (!current || !html) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-sk-cart-drawer-content]');
    if (!fresh) return false;
    // Keep keyboard focus on the same control (or the close button if its line is gone).
    const focusKey = current.contains(document.activeElement) ? document.activeElement.dataset.skFocus : null;
    current.replaceWith(fresh);
    // An empty cart shows the bare icon, not a 0.
    document.querySelectorAll('.cart_count').forEach(count => { count.textContent = fresh.dataset.count === '0' ? '' : fresh.dataset.count; });
    if (focusKey) (fresh.querySelector(`[data-sk-focus="${CSS.escape(focusKey)}"]`) || fresh.querySelector('[data-sk-focus="close"]'))?.focus();
    return true;
  }
  const refreshCart = () => fetch(`${shopRoot}?sections=${cartSection}`)
    .then(response => response.json())
    .then(sections => { if (!renderCart(sections)) throw new Error('cart drawer render failed'); });
  const setSending = (button, sending) => {
    if (!button) return;
    button.classList.toggle('sk-is-sending', sending);
    if (sending) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  };
  function showAddError(form, button, message) {
    let note = form.querySelector('[data-sk-add-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'sk-add-error';
      note.setAttribute('role', 'alert');
      note.dataset.skAddError = '';
      if (button && form.contains(button)) button.after(note);
      else form.append(note);
    }
    note.textContent = message || cartDrawer.dataset.addErrorMessage || '';
    note.hidden = false;
    const box = note.getBoundingClientRect();
    if (box.top < 0 || box.bottom > innerHeight) note.scrollIntoView({ block: 'center', behavior: dialogReducedMotion.matches ? 'auto' : 'smooth' });
  }
  // Product page add-ons (snippets/product.liquid, [data-sk-addons]): switched-on add-ons join the main item in one
  // cart/add.js request ({ items: [...] }), so the drawer opens once with everything. The main item keeps its
  // quantity, line item properties and selling plan from the form. Without add-ons the form's own fields are posted.
  function addRequestBody(form) {
    const scope = form.closest('[data-sk-product-scope]');
    const addons = scope ? [...scope.querySelectorAll('input[data-sk-addon-toggle]:checked')]
      .map(input => Number(input.dataset.variantId))
      .filter(id => Number.isInteger(id) && id > 0) : [];
    const fields = new FormData(form);
    const mainId = Number(fields.get('id'));
    if (!addons.length || !Number.isInteger(mainId) || mainId <= 0) {
      fields.append('sections', cartSection);
      fields.append('sections_url', location.pathname);
      return fields;
    }
    const main = { id: mainId, quantity: Math.max(1, parseInt(fields.get('quantity'), 10) || 1) };
    const properties = {};
    fields.forEach((value, name) => {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match && typeof value === 'string') properties[match[1]] = value;
    });
    if (Object.keys(properties).length) main.properties = properties;
    const plan = fields.get('selling_plan');
    if (plan) main.selling_plan = Number(plan);
    return {
      items: [main, ...addons.map(id => ({ id, quantity: 1 }))],
      sections: [cartSection],
      sections_url: location.pathname
    };
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!cartDrawer || event.defaultPrevented || !form.matches?.('form[action*="/cart/add"]')) return;
    const trigger = document.querySelector(`[data-sk-open="${cartDrawer.id}"]`);
    if (!trigger) return; // the cart page keeps the normal post
    event.preventDefault();
    const button = event.submitter || form.querySelector('[name="add"]');
    if (button?.getAttribute('aria-busy') === 'true') return;
    form.querySelector('[data-sk-add-error]')?.setAttribute('hidden', '');
    setSending(button, true);
    cartRequest('cart/add.js', addRequestBody(form))
      .then(
        data => Promise.resolve(renderCart(data.sections) || refreshCart())
          .then(() => open(cartDrawer, button || trigger))
          .catch(() => location.assign(trigger.href)), // added, but the drawer couldn't show it: go to the cart
        error => {
          if (error.status) showAddError(form, button, error.message);
          else HTMLFormElement.prototype.submit.call(form);
        }
      )
      .finally(() => setSending(button, false));
  });
  let cartChanging = false;
  const cartQueued = new Map(); // rapid taps: the latest target per item waits, and runs against the fresh render
  function changeLine(line, quantity, key) {
    const content = cartDrawer?.querySelector('[data-sk-cart-drawer-content]');
    if (!content) return;
    if (cartChanging) { if (key) { cartQueued.delete(key); cartQueued.set(key, [line, quantity, key]); } return; }
    cartChanging = true;
    content.setAttribute('aria-busy', 'true');
    const showError = message => {
      const note = cartDrawer.querySelector('[data-sk-cart-drawer-error]');
      if (!note) return;
      note.textContent = message || cartDrawer.dataset.errorMessage || '';
      note.hidden = false;
    };
    cartRequest('cart/change.js', { line: Number(line), quantity: Math.max(0, parseInt(quantity, 10) || 0), sections: [cartSection], sections_url: location.pathname })
      .then(data => { if (!renderCart(data.sections)) return refreshCart(); })
      .catch(error => refreshCart()
        .catch(() => content.removeAttribute('aria-busy'))
        .then(() => showError(error.status ? error.message : '')))
      .finally(() => {
        cartChanging = false;
        // A queued tap is matched to its item by key, because a removal shifts the line positions; it is
        // applied only when that item is still shown and its target differs from what is now shown.
        // Entries run one at a time: each response re-enters here and takes the next one still waiting.
        while (cartQueued.size) {
          const [key, next] = cartQueued.entries().next().value;
          cartQueued.delete(key);
          const input = cartDrawer.querySelector(`[data-sk-cart-quantity][data-sk-focus="qty-${key}"]`);
          if (input && String(input.value) !== String(next[1])) { changeLine(input.dataset.line, next[1], key); break; }
        }
      });
  }
  cartDrawer?.addEventListener('click', event => {
    const control = event.target.closest('[data-sk-cart-change]');
    if (!control) return;
    // Each tap steps from the number on screen (not the last render), so rapid taps add up; minus at 1 removes.
    const input = cartDrawer.querySelector(`[data-sk-cart-quantity][data-line="${control.dataset.line}"]`);
    if (!input) return;
    const step = Math.sign(Number(control.dataset.quantity) - Number(input.defaultValue));
    const target = Math.max(0, (parseInt(input.value, 10) || 0) + step);
    input.value = target;
    changeLine(control.dataset.line, target, lineKey(input));
  });
  function lineKey(input) { return (input.dataset.skFocus || '').replace(/^qty-/, ''); }

  // ---------------------------------------------------------------------------------------------
  // Account hub (signed-in account drawer, snippets/sekage-navigation.liquid): For you | Orders |
  // Profile tabs, plus the Recently viewed and Saved from cart rails on For you. Product data comes
  // from Shopify's /products/{handle}.js and /cart.js; all text is set with textContent. Signed out,
  // none of these elements exist and everything below does nothing (except remembering the product
  // being viewed, which needs no markup beyond #sk-current-product).
  // ---------------------------------------------------------------------------------------------
  const recentKey = 'sk-recently-viewed';
  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(recentKey) || '[]');
      return Array.isArray(list) ? list.filter(handle => typeof handle === 'string' && handle) : [];
    } catch { return []; }
  };
  const writeRecent = list => { try { localStorage.setItem(recentKey, JSON.stringify(list.slice(0, 12))); } catch {} };
  const currentHandle = (() => {
    const node = document.getElementById('sk-current-product');
    if (!node) return '';
    try { return String(JSON.parse(node.textContent).handle || ''); } catch { return ''; }
  })();
  // Newest first, unique, at most 12.
  if (currentHandle) writeRecent([currentHandle, ...readRecent().filter(handle => handle !== currentHandle)]);

  const formatMoney = cents => {
    const currency = window.Shopify?.currency?.active || 'USD';
    const amount = (Number(cents) || 0) / 100;
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(amount); }
    catch { return amount.toFixed(2); }
  };
  // Same-origin paths only, so a card can never point anywhere else.
  const localUrl = value => {
    try {
      const url = new URL(value, location.origin);
      return url.origin === location.origin ? url.pathname + url.search : '';
    } catch { return ''; }
  };
  const sizedImage = value => {
    const src = typeof value === 'string' ? value : value?.src || value?.url || '';
    if (!src) return '';
    try {
      const url = new URL(src, location.href);
      url.searchParams.set('width', '300');
      return url.href;
    } catch { return src; }
  };
  function accountCard({ url, image, title, price }) {
    const href = localUrl(url);
    if (!href) return null;
    const card = document.createElement('a');
    card.className = 'sk-account__card';
    card.href = href;
    const media = document.createElement('span');
    media.className = 'sk-account__card-media';
    const src = sizedImage(image);
    if (src) {
      const img = document.createElement('img');
      img.className = 'sk-account__card-img';
      img.src = src;
      img.alt = ''; // the title below names the link
      img.width = 300;
      img.height = 300;
      img.loading = 'lazy';
      img.decoding = 'async';
      media.append(img);
    }
    const name = document.createElement('span');
    name.className = 'sk-account__card-title';
    name.textContent = title || '';
    const cost = document.createElement('span');
    cost.className = 'sk-account__card-price';
    cost.textContent = formatMoney(price);
    card.append(media, name, cost);
    return card;
  }
  const getJson = path => fetch(shopRoot + path, { headers: { Accept: 'application/json' } });

  async function renderRecent(hub) {
    const list = hub.querySelector('[data-sk-recent-list]');
    if (!list) return;
    const empty = hub.querySelector('[data-sk-recent-empty]');
    const handles = readRecent().filter(handle => handle !== currentHandle).slice(0, 10);
    if (empty && handles.length) empty.hidden = true; // no "nothing yet" flash while products load
    const gone = [];
    const products = await Promise.all(handles.map(handle => getJson(`products/${encodeURIComponent(handle)}.js`)
      .then(response => {
        if (response.status === 404) { gone.push(handle); return null; }
        return response.ok ? response.json() : null;
      })
      .catch(() => null)));
    if (gone.length) writeRecent(readRecent().filter(handle => !gone.includes(handle)));
    const cards = products.filter(Boolean).map(product => accountCard({
      url: product.url,
      image: product.featured_image,
      title: product.title,
      price: product.price
    })).filter(Boolean);
    list.replaceChildren(...cards);
    if (empty) empty.hidden = cards.length > 0;
  }
  async function renderSaved(hub) {
    const list = hub.querySelector('[data-sk-saved-list]');
    if (!list) return;
    const empty = hub.querySelector('[data-sk-saved-empty]');
    let cart;
    try {
      const response = await getJson('cart.js');
      if (!response.ok) return;
      cart = await response.json();
    } catch { return; } // keep whatever is already shown
    const cards = (cart.items || []).map(item => accountCard({
      url: item.url,
      image: item.image || item.featured_image,
      title: item.product_title || item.title,
      price: item.final_line_price ?? item.final_price
    })).filter(Boolean);
    list.replaceChildren(...cards);
    if (empty) empty.hidden = cards.length > 0;
  }
  // Buy again (order view): adds the order's items to the cart without leaving the drawer, shows a brief
  // "Added" on the button, then refreshes the header count, the cart drawer and the Saved from cart rail.
  const buyAgainTimers = new WeakMap();
  function buyAgain(button, hub) {
    if (button.getAttribute('aria-busy') === 'true') return;
    let items;
    try { items = JSON.parse(button.dataset.skBuyAgainItems || '[]'); } catch { items = []; }
    items = (Array.isArray(items) ? items : [])
      .map(item => ({ id: Number(item?.id), quantity: Math.max(1, parseInt(item?.quantity, 10) || 1) }))
      .filter(item => Number.isInteger(item.id) && item.id > 0);
    if (button.dataset.skLabel === undefined) button.dataset.skLabel = button.textContent.trim();
    button.setAttribute('aria-live', 'polite');
    let note = button.parentElement?.querySelector('[data-sk-buy-again-error]');
    const showError = message => {
      if (!note) {
        note = document.createElement('p');
        note.className = 'sk-account__action-error';
        note.setAttribute('role', 'alert');
        note.dataset.skBuyAgainError = '';
        button.after(note);
      }
      note.textContent = message || button.dataset.skErrorLabel || cartDrawer?.dataset.addErrorMessage || 'Could not add these items. Please try again.';
      note.hidden = false;
    };
    if (note) note.hidden = true;
    clearTimeout(buyAgainTimers.get(button));
    button.textContent = button.dataset.skLabel;
    if (!items.length) { showError(); return; }
    setSending(button, true);
    cartRequest('cart/add.js', { items, sections: [cartSection], sections_url: location.pathname })
      .then(data => {
        button.textContent = button.dataset.skAddedLabel || 'Added';
        buyAgainTimers.set(button, setTimeout(() => { button.textContent = button.dataset.skLabel; }, 2500));
        return Promise.resolve(renderCart(data.sections) || refreshCart()).catch(() => {})
          .then(() => renderSaved(hub));
      }, error => showError(error.status ? error.message : ''))
      .finally(() => setSending(button, false));
  }

  function initAccountHub(root = document) {
    root.querySelectorAll('.sk-account').forEach(hub => {
      if (hub.dataset.skAccountReady) return;
      hub.dataset.skAccountReady = 'true';
      const tabs = Array.from(hub.querySelectorAll('[data-sk-account-tab]'));
      const panels = Array.from(hub.querySelectorAll('[data-sk-account-panel]'));
      const views = Array.from(hub.querySelectorAll('[data-sk-account-view-id]'));
      const scroller = hub.querySelector('.sk-account__panels');
      // Subview state: the open order view, the control that opened it and the list's scroll position.
      let openView = null;
      let opener = null;
      let parentScroll = 0;
      const markTab = tab => tabs.forEach(item => {
        const on = item === tab;
        item.setAttribute('aria-selected', String(on));
        item.tabIndex = on ? 0 : -1;
      });
      const hideViews = () => {
        views.forEach(view => { view.hidden = true; });
        openView = null;
      };
      // Tabs: the selected tab is the only one in the Tab order; arrows, Home and End move between them.
      // Choosing a tab always leaves any open order view.
      const select = (tab, focus) => {
        hideViews();
        markTab(tab);
        panels.forEach(section => { section.hidden = section.dataset.skAccountPanel !== tab.dataset.skAccountTab; });
        if (scroller) scroller.scrollTop = 0;
        if (focus) tab.focus();
      };
      tabs.forEach(tab => tab.addEventListener('click', () => select(tab)));
      // Order views open inside the drawer in place of their parent panel; the parent tab stays selected.
      const showView = (id, from) => {
        const view = views.find(item => item.dataset.skAccountViewId === id);
        if (!view) return;
        const parent = view.dataset.skAccountParent;
        if (!openView && scroller) parentScroll = scroller.scrollTop;
        hideViews();
        panels.forEach(section => { section.hidden = true; });
        view.hidden = false;
        openView = view;
        opener = from || null;
        const parentTab = tabs.find(tab => tab.dataset.skAccountTab === parent);
        if (parentTab) markTab(parentTab);
        if (scroller) scroller.scrollTop = 0;
        (view.querySelector('.sk-account__back') || view).focus({ preventScroll: true });
      };
      const closeView = restoreFocus => {
        if (!openView) return;
        const parent = openView.dataset.skAccountParent;
        hideViews();
        panels.forEach(section => { section.hidden = section.dataset.skAccountPanel !== parent; });
        if (scroller) scroller.scrollTop = parentScroll;
        if (restoreFocus && opener?.isConnected && !opener.closest('[hidden]')) opener.focus({ preventScroll: true });
        opener = null;
      };
      // Owner rule: nothing in the signed-in drawer leaves the storefront (log out excepted).
      const leavesStore = link => {
        if (link.matches('.sk-account__logout, [data-sk-allow-external]')) return false;
        try {
          const url = new URL(link.href, location.href);
          return (url.protocol === 'http:' || url.protocol === 'https:') && url.host !== location.host;
        } catch { return false; }
      };
      hub.addEventListener('auxclick', event => {
        const link = event.target.closest('a[href]');
        if (link && hub.contains(link) && leavesStore(link)) event.preventDefault();
      });
      hub.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (link && hub.contains(link) && leavesStore(link)) { event.preventDefault(); return; }
        // The order row's thumbnails open the order too (same as its button).
        const summaryThumbs = event.target.closest('.sk-account__thumbs');
        const trigger = event.target.closest('[data-sk-account-view]')
          || (summaryThumbs && summaryThumbs.closest('.sk-account__order-summary')?.querySelector('[data-sk-account-view]'));
        if (trigger && hub.contains(trigger)) { event.preventDefault(); showView(trigger.dataset.skAccountView, trigger); return; }
        if (event.target.closest('.sk-account__back')) { closeView(true); return; }
        const again = event.target.closest('[data-sk-buy-again]');
        if (again && hub.contains(again)) buyAgain(again, hub);
      });
      hub.querySelector('[role="tablist"]')?.addEventListener('keydown', event => {
        const index = tabs.indexOf(event.target);
        if (index < 0) return;
        const last = tabs.length - 1;
        const next = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        select(tabs[next], true);
      });
      // Rails load each time the drawer opens (not on tab switches). The last selected tab is kept, but an
      // order view left open is closed, so reopening shows that tab's panel.
      const dialog = hub.closest('dialog');
      if (!dialog) return;
      const hasRails = !!hub.querySelector('[data-sk-recent-list], [data-sk-saved-list]');
      const load = () => {
        closeView(false);
        if (hasRails) { renderRecent(hub); renderSaved(hub); }
      };
      let wasOpen = dialog.open;
      new MutationObserver(() => {
        if (dialog.open && !wasOpen) load();
        wasOpen = dialog.open;
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      if (wasOpen) load();
    });
  }
  initAccountHub();
  document.addEventListener('shopify:section:load', event => initAccountHub(event.target));

  const footerPhone = matchMedia('(max-width: 767px)');
  function prepareFooter(root = document) {
    root.querySelectorAll('[data-sk-footer-menu]').forEach(menu => {
      if (menu.dataset.skFooterReady) return;
      menu.dataset.skFooterReady = 'true';
      menu.open = true;
      menu.addEventListener('click', event => {
        // Menus are always open (phones show a 2-column list, desktop a static column).
        if (event.target.closest('summary')) event.preventDefault();
      });
    });
    root.querySelectorAll('[data-sk-email-error]').forEach(input => {
      if (input.dataset.skValidationReady) return;
      const feedback = document.getElementById(input.getAttribute('aria-describedby'));
      if (!feedback) return;
      input.dataset.skValidationReady = 'true';
      input.addEventListener('invalid', event => {
        event.preventDefault();
        input.setAttribute('aria-invalid', 'true');
        feedback.textContent = input.dataset.skEmailError;
        feedback.hidden = false;
        input.focus();
      });
      input.addEventListener('input', () => {
        if (!input.validity.valid) return;
        input.removeAttribute('aria-invalid');
        feedback.hidden = true;
        feedback.textContent = '';
      });
    });
  }
  // Country, currency and language lists open at the selected option: the list's own scroll container is
  // moved (never the page), and focus lands on the selected option. Turbo opens the list by setting
  // aria-expanded="true" on the toggle, so that attribute is the signal.
  function prepareLocalization(root = document) {
    root.querySelectorAll('[data-disclosure-toggle]').forEach(toggle => {
      if (toggle.dataset.skLocalizationReady) return;
      toggle.dataset.skLocalizationReady = 'true';
      const reveal = () => {
        if (toggle.getAttribute('aria-expanded') !== 'true') return;
        const list = toggle.parentElement?.querySelector('[data-disclosure-list]');
        const current = list?.querySelector('[aria-current="true"]') || list?.querySelector('.disclosure-list__item--current .disclosure__button');
        if (!list || !current) return;
        const place = () => {
          const listBox = list.getBoundingClientRect();
          const itemBox = current.getBoundingClientRect();
          if (!listBox.height) return false;
          list.scrollTop += (itemBox.top - listBox.top) - (list.clientHeight - itemBox.height) / 2;
          return true;
        };
        // The list is display:none until Turbo's class lands; wait one frame so it has a box to measure.
        requestAnimationFrame(() => {
          if (toggle.getAttribute('aria-expanded') !== 'true' || !place()) return;
          current.focus({ preventScroll: true });
        });
      };
      new MutationObserver(reveal).observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
    });
  }
  prepareLocalization();
  document.addEventListener('shopify:section:load', event => prepareLocalization(event.target));

  prepareFooter();
  footerPhone.addEventListener('change', () => {
    document.querySelectorAll('[data-sk-footer-menu]').forEach(menu => { menu.open = true; });
  });
  document.addEventListener('shopify:section:load', event => prepareFooter(event.target));
})();
